import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IConditionalValidations,
  IFormFieldsValidationModel,
  IPatternValidation,
} from '../../../../models/form-fields-formly-validation.model';
import {
  EIremboFormlyFieldSubTypes,
  EIremboFormlyFieldTypes,
} from '@irembo-andela/irembogov3-common';
import {
  EAvailableConditionalValidations,
  EConditionalPropertyType,
  EFormBuilderConditionalFieldComparator,
  EFormBuilderConditionComparator,
  IConditionalValidationConfig,
  IFieldConditionProperty,
  IFormFieldsConditionRule,
  IUtilValidationConfig,
} from '../../../../models/form-fields-formly-conditional-validation.model';
import { NonDataElementFormlyFieldTypes } from '../../../../models/irembo-formly-non-data-field-types.enum';
import { FieldValidationPatternConfigEnumToRegexString } from '../../../field-validation-pattern-config-conversion.utils';

interface IValueExpressionsAndMessages {
  value: unknown;
  expression: string;
  errorMessage?: string;
}

interface IGenericUtilValidatorConfig {
  name: 'genericUtilValidator' | 'asyncFormStateUtilValidator';
  options: {
    prop1: unknown;
    prop2: unknown;
    comparator: string;
    validationName: string;
  };
}
interface IQueryCollation {
  property1Exp: string;
  conditonExp?: string;
  property2Exp?: string;
  stringCompare?: boolean;
  undefinedCompare?: boolean;
}

export const setUtilConditionalExpressionFromModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  if (!Array.isArray(model?.utilValidations)) {
    delete field?.props?.['utilValidationsModel'];
    return field;
  }

  const genericValidations: IGenericUtilValidatorConfig[] = [];
  const asyncGenericValidations: IGenericUtilValidatorConfig[] = [];
  const validationMessages: Record<string, string> = {};

  model.utilValidations.forEach((validation: IUtilValidationConfig) => {
    const utilQueryCollation: IQueryCollation = conditionalExpressionBuilder(
      validation.condition,
      [],
      model.configuredFieldRootModelPath,
      false
    )[0];
    const property1FieldType =
      validation.condition.property1.propertyField?.field.fieldType;

    const utilValidationConfig: IGenericUtilValidatorConfig = {
      name: 'genericUtilValidator',
      options: {
        prop1: splitPropertValueToParts(utilQueryCollation.property1Exp),
        prop2: utilQueryCollation.undefinedCompare
          ? undefined
          : splitPropertValueToParts(utilQueryCollation.property2Exp) ?? true,
        comparator: utilQueryCollation.conditonExp ?? '===',
        validationName: <string>validation.validationName,
      },
    };

    if (
      NonDataElementFormlyFieldTypes.includes(
        <EIremboFormlyFieldTypes>property1FieldType
      )
    ) {
      utilValidationConfig.name = 'asyncFormStateUtilValidator';
      asyncGenericValidations.push(utilValidationConfig);
    } else {
      genericValidations.push(utilValidationConfig);
    }
    validationMessages[<string>validation.validationName] = <string>(
      validation.validationMessage
    );
  });

  if (field.validators?.validation) {
    field.validators.validation = genericValidations;
  } else {
    field.validators = {
      validation: genericValidations,
    };
  }

  if (field.asyncValidators?.validation) {
    field.asyncValidators.validation = asyncGenericValidations;
  } else {
    field.asyncValidators = {
      validation: asyncGenericValidations,
    };
  }

  if (field.validation) {
    field.validation['messages'] = {
      ...field.validation.messages,
      ...validationMessages,
    };
  } else {
    field.validation = {
      messages: validationMessages,
    };
  }

  if (field?.props) {
    field.props['utilValidationsModel'] = model.utilValidations;
  } else {
    field.props = {
      utilValidationsModel: model.utilValidations,
    };
  }
  return field;
};

export const setConditionalExpressionFromModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  if (
    !model?.conditionalValidations ||
    typeof model?.conditionalValidations !== 'object'
  ) {
    delete field?.props?.['conditionalValidationsModel'];
    return field;
  }

  Object.keys(model.conditionalValidations).forEach((key: string) => {
    handleConditionalValidationTypes(key, field, model);
  });

  if (field?.props) {
    field.props['conditionalValidationsModel'] = model.conditionalValidations;
  } else {
    field.props = {
      conditionalValidationsModel: model.conditionalValidations,
    };
  }
  return field;
};

export const setConditionalValidationExpressionOnField = (
  queryFunction: string,
  field: FormlyFieldConfig,
  expressionKey: string
): FormlyFieldConfig => {
  if (field?.expressions) {
    field.expressions[expressionKey] = String(queryFunction);
  } else {
    field.expressions = {
      expressionKey: String(queryFunction),
    };
  }
  return field;
};

const handleConditionalValidationTypes = (
  validationType: string,
  field: FormlyFieldConfig,
  model: IFormFieldsValidationModel
): FormlyFieldConfig => {
  const configuredFieldRootModelPath: string | null | undefined =
    model.configuredFieldRootModelPath;
  const conditionalValidations: IConditionalValidations | undefined =
    model.conditionalValidations;
  if (
    !(conditionalValidations as unknown as Record<string, unknown>)?.[
      validationType
    ]
  ) {
    return field;
  }

  switch (validationType) {
    case EAvailableConditionalValidations.required: {
      if (!conditionalValidations?.required?.conditions?.[0]) {
        // No conditions set for 'Required' validation
        return field;
      }

      let queryExpression = conditionalValidationConfigToExpression(
        conditionalValidations?.required,
        configuredFieldRootModelPath
      );

      if (conditionalValidations?.required?.validation === false) {
        queryExpression = `!(${queryExpression})`;
      }

      return setConditionalValidationExpressionOnField(
        queryExpression,
        field,
        'props.required'
      );
    }
    case EAvailableConditionalValidations.minLength:
    case EAvailableConditionalValidations.maxLength:
    case EAvailableConditionalValidations.patternValidation: {
      let configs: IConditionalValidationConfig[] | undefined = undefined;
      let expressionKey = `props.${validationType}`;
      let validationMessageExpressionKey = `validation.messages['${validationType}']`;
      if (validationType === EAvailableConditionalValidations.minLength) {
        configs = conditionalValidations?.minLength;
      }
      if (validationType === EAvailableConditionalValidations.maxLength) {
        configs = conditionalValidations?.maxLength;
      }
      if (
        validationType === EAvailableConditionalValidations.patternValidation
      ) {
        expressionKey = 'props.pattern';
        validationMessageExpressionKey = `validation.messages['pattern']`;
        configs = conditionalValidations?.patternValidation;
      }
      if (!configs?.[0]) {
        // No conditions set for validation
        return field;
      }

      const conditonExpressions: IValueExpressionsAndMessages[] = [];

      configs?.forEach((validationConfig: IConditionalValidationConfig) => {
        if (!validationConfig?.conditions) {
          return;
        }

        let validationValue = validationConfig?.validation;

        if (
          validationType === EAvailableConditionalValidations.patternValidation
        ) {
          const regExPattern = FieldValidationPatternConfigEnumToRegexString(
            (validationConfig?.validation as IPatternValidation)?.patternType,
            (validationConfig?.validation as IPatternValidation)?.customPattern
          );
          validationValue = `"${regExPattern}"`;
        }

        conditonExpressions.push({
          value: validationValue,
          expression: conditionalValidationConfigToExpression(
            validationConfig,
            configuredFieldRootModelPath
          ),
          errorMessage: validationConfig.useCustomErrorMessage
            ? `formState.getTranslation('${validationConfig.errorMessage}')`
            : undefined,
        });
      });

      const defaultExpressionValue = model[validationType] ?? undefined;
      const defaultErrorMessage =
        model.validationMessages?.[validationType] ?? 'Field is invalid.';
      let finalExpression = defaultExpressionValue as unknown as string;
      let validationMessageExpression = `formState.getTranslation('${defaultErrorMessage}')`;

      conditonExpressions
        .reverse()
        .forEach((item: IValueExpressionsAndMessages) => {
          finalExpression = `${item.expression} ? ${item.value} : ${finalExpression}`;

          if (item.errorMessage) {
            validationMessageExpression = `${item.expression} ? ${item.errorMessage} : ${validationMessageExpression}`;
          }
        });

      setConditionalValidationExpressionOnField(
        finalExpression,
        field,
        expressionKey
      );
      setConditionalValidationExpressionOnField(
        validationMessageExpression,
        field,
        validationMessageExpressionKey
      );
    }
  }

  return field;
};

export const conditionalValidationConfigToExpression = (
  config?: IConditionalValidationConfig,
  configuredFieldRootModelPath?: string | null
): string => {
  const orFieldsConditionRules: IFormFieldsConditionRule[] = [];
  const andFieldsConditionRules: IFormFieldsConditionRule[] = [];

  config?.conditions?.forEach((rule: IFormFieldsConditionRule) => {
    if (!rule.condition) {
      rule.condition = EFormBuilderConditionComparator.OR;
    }
    if (rule.condition === EFormBuilderConditionComparator.OR) {
      orFieldsConditionRules.push(rule);
    }
    if (rule.condition === EFormBuilderConditionComparator.AND) {
      andFieldsConditionRules.push(rule);
    }
  });

  const andQueries: IQueryCollation[] = [];
  const orQueries: IQueryCollation[] = [];

  orFieldsConditionRules.forEach(actionConditionRule => {
    conditionalExpressionBuilder(
      actionConditionRule,
      orQueries,
      configuredFieldRootModelPath
    );
  });

  andFieldsConditionRules.forEach(actionConditionRule => {
    conditionalExpressionBuilder(
      actionConditionRule,
      andQueries,
      configuredFieldRootModelPath
    );
  });

  return queryGroupsToExpressionString(orQueries, andQueries);
};

export const conditionalExpressionBuilder = (
  validationRule: IFormFieldsConditionRule,
  collationArray: IQueryCollation[],
  configuredFieldRootModelPath?: string | null,
  safeFormatValue = true
): IQueryCollation[] => {
  let expCondition = '';
  const property1Value = getPropertyExpressionValueByType(
    validationRule.property1,
    validationRule.comparator,
    configuredFieldRootModelPath,
    safeFormatValue
  );
  const property2Value = getPropertyExpressionValueByType(
    validationRule.property2,
    validationRule.comparator,
    configuredFieldRootModelPath,
    safeFormatValue
  );
  switch (validationRule.comparator) {
    case EFormBuilderConditionalFieldComparator.IS_DEFINED: {
      expCondition = '!==';
      break;
    }
    case EFormBuilderConditionalFieldComparator.IS_UNDEFINED: {
      expCondition = '===';
      break;
    }
    case EFormBuilderConditionalFieldComparator.IS_BOOLEAN_TRUE: {
      expCondition = '===';
      break;
    }
    case EFormBuilderConditionalFieldComparator.IS_ABOVE: {
      expCondition = validationRule.useOrEqualTo ? '>=' : '>';
      break;
    }
    case EFormBuilderConditionalFieldComparator.IS_BELOW: {
      expCondition = validationRule.useOrEqualTo ? '<=' : '<';
      break;
    }
    case EFormBuilderConditionalFieldComparator.IS_EQUAL_TO: {
      expCondition = validationRule.strictMode ? '===' : '==';
      break;
    }
    case EFormBuilderConditionalFieldComparator.IS_NOT_EQUAL_TO: {
      expCondition = validationRule.strictMode ? '!==' : '!=';
      break;
    }
  }

  if (
    validationRule.comparator ===
    EFormBuilderConditionalFieldComparator.IS_VALID
  ) {
    const fieldRefPath =
      validationRule.property1.propertyField?.field?.fieldRefPath;
    const validity = property2Value ? 'valid' : 'invalid';
    if (fieldRefPath) {
      const rootModelPath = configuredFieldRootModelPath
        ? configuredFieldRootModelPath?.replace('?.model', '?.form')
        : 'field?.parent?.parent?.form';

      const controlPath = fieldRefPath.replace(/\?/g, '');
      collationArray.push({
        property1Exp: `${rootModelPath}?.get?.('${controlPath}')?.${validity}`,
      });
    } else {
      collationArray.push({
        property1Exp: `${configuredFieldRootModelPath}?.${validationRule.property1.propertyField?.field?.fieldRefPath}?.${validity}`,
      });
    }
    return collationArray;
  }

  if (
    property1Value &&
    [
      EFormBuilderConditionalFieldComparator.IS_UNDEFINED,
      EFormBuilderConditionalFieldComparator.IS_DEFINED,
    ].includes(
      <EFormBuilderConditionalFieldComparator>validationRule.comparator
    )
  ) {
    collationArray.push({
      property1Exp: property1Value,
      conditonExp: expCondition,
      property2Exp: undefined,
      undefinedCompare: true,
    });
    return collationArray;
  }

  if (!(property1Value && expCondition && property2Value)) {
    return collationArray;
  }

  if (
    validationRule.property1.propertyField?.field.fieldType ===
    EIremboFormlyFieldTypes.multicheckbox
  ) {
    if (
      validationRule.comparator ===
      EFormBuilderConditionalFieldComparator.IS_NOT_EQUAL_TO
    ) {
      collationArray.push({
        property1Exp: `!${property1Value}?.includes(${property2Value})`,
      });
    } else {
      collationArray.push({
        property1Exp: `${property1Value}?.includes(${property2Value})`,
      });
    }
    return collationArray;
  }
  if (
    (expCondition === '!=' || expCondition === '==') &&
    (typeof property1Value === 'string' || typeof property2Value === 'string')
  ) {
    collationArray.push({
      property1Exp: property1Value,
      conditonExp: expCondition,
      property2Exp: property2Value,
      stringCompare: true,
    });
  } else {
    collationArray.push({
      property1Exp: property1Value,
      conditonExp: expCondition,
      property2Exp: property2Value,
    });
  }

  return collationArray;
};

export const getPropertyExpressionValueByType = (
  property: IFieldConditionProperty,
  comparator: EFormBuilderConditionalFieldComparator,
  configuredFieldRootModelPath?: string | null,
  safeFormatValue = true
): string | undefined => {
  if (property.propertyType === EConditionalPropertyType.formStateValue) {
    let pathList = `formState?.${property.propertyFormStateValuePath?.formStateKey}`;
    if (property.propertyFormStateValuePath?.fieldKey) {
      pathList = `${pathList}?.${property.propertyFormStateValuePath?.fieldKey}`;
    }
    if (property.propertyFormStateValuePath?.valuePath) {
      pathList = `${pathList}?.${property.propertyFormStateValuePath?.valuePath}`;
    }
    return pathList;
  }

  if (property.propertyType === EConditionalPropertyType.self) {
    if (comparator === EFormBuilderConditionalFieldComparator.IS_BOOLEAN_TRUE) {
      const booleanValue = property.propertyValues?.booleanValue ? true : false;
      return safeFormatComparedValueForExpressions(
        booleanValue,
        safeFormatValue
      );
    }

    if (
      [
        EFormBuilderConditionalFieldComparator.IS_UNDEFINED,
        EFormBuilderConditionalFieldComparator.IS_DEFINED,
      ].includes(<EFormBuilderConditionalFieldComparator>comparator)
    ) {
      return safeFormatComparedValueForExpressions(true, safeFormatValue);
    }

    if (comparator === EFormBuilderConditionalFieldComparator.IS_VALID) {
      const isValid = property.propertyValues?.validityValue ? true : false;
      return safeFormatComparedValueForExpressions(isValid, safeFormatValue);
    }

    return safeFormatComparedValueForExpressions(
      property.propertyValues?.isValue,
      safeFormatValue
    );
  }

  if (
    property.propertyField?.field?.fieldType &&
    [
      EIremboFormlyFieldTypes.customdropdown,
      EIremboFormlyFieldTypes.customdropdownpaginated,
    ].includes(
      property.propertyField.field.fieldType as EIremboFormlyFieldTypes
    ) &&
    [
      EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
      EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET_PAGINATED,
    ].includes(property.propertyField.field.fieldSubType)
  ) {
    return `${configuredFieldRootModelPath}?.${property.propertyField?.field?.fieldRefPath}?.value`;
  }
  return `${configuredFieldRootModelPath}?.${property.propertyField?.field?.fieldRefPath}`;
};

const queryGroupsToExpressionString = (
  orQueries: IQueryCollation[],
  andQueries: IQueryCollation[]
): string => {
  const orExpressionPart: string | null = collationArrayToExpression(
    '||',
    orQueries
  );
  const andExpressionPart: string | null = collationArrayToExpression(
    '&&',
    andQueries
  );

  if (!(orExpressionPart || andExpressionPart)) {
    return '';
  }

  if (!orExpressionPart && andExpressionPart) {
    return andExpressionPart;
  }

  if (!andExpressionPart && orExpressionPart) {
    return orExpressionPart;
  }

  return `(${orExpressionPart}) && (${andExpressionPart})`;
};

const collationArrayToExpression = (
  comparatorOperation: '||' | '&&',
  collationArray?: IQueryCollation[]
): string | null => {
  if (!collationArray) {
    return null;
  }
  const expressionParts: string[] = collationArray.map(
    (collation: IQueryCollation) => {
      let expression: string = collation.property1Exp;
      if (collation.conditonExp) {
        if (collation.stringCompare) {
          expression = `String(${collation.property1Exp})?.toLowerCase() ${collation.conditonExp} String(${collation.property2Exp})?.toLowerCase()`;
        } else {
          expression += ` ${collation.conditonExp} ${collation.property2Exp}`;
        }
      }
      return expression;
    }
  );

  if (expressionParts.length < 1) {
    return null;
  }

  return expressionParts.join(` ${comparatorOperation} `);
};

const safeFormatComparedValueForExpressions = (
  value: any,
  safeFormatValue: boolean
) => {
  if (!safeFormatValue) {
    return value;
  }
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === 'null' ||
    value === 'undefined' ||
    value === 'true' ||
    value === 'false' ||
    value === 'valid' ||
    value === 'invalid'
  ) {
    return value;
  }
  return `'${value}'`;
};

const splitPropertValueToParts = (value: unknown) => {
  if (
    typeof value === 'string' &&
    (value.includes('?.') || value.includes('.'))
  ) {
    const propValueArray = value.split('?.');
    if (propValueArray?.[0] === 'formState') {
      propValueArray.unshift('options');
    }

    /** handle nested paths like 'userLocation.city' */
    const expandedPropValueArray: string[] = [];

    propValueArray.forEach((item: string) => {
      const parts = item.split('.');
      expandedPropValueArray.push(...parts);
    });

    return expandedPropValueArray;
  }

  return value;
};
